import numbro from 'numbro';
import { inject, injectable } from 'tsyringe';
import {
    AccountType,
    CryptoOrderTransaction,
    OrderActualTransaction,
    OrderMarket,
    OrderMarketTime,
    OrderStatus,
    OrderTransaction,
    OrderType,
    OrderValidity,
} from '../../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../../constants/tokens';
import {
    Apis,
    CancelledCryptoOrderInfo,
    CryptoTradePreview,
    CryptoTradingFormValues,
    MiddlewareConfig,
    OrderWithExtraInfo,
    TradingFormMeta,
    TradingFormValues,
} from '../../types';
import { divide, getUuid, multiply, parseCryptoSymbol, subtract, sum } from '../../utils';
import { QuoteApi } from '../QuoteApi';
import { TradingApi } from '../TradingApi';
import { UserApi } from '../UserApi';
export interface AxosCryptoTradingValues {
    instrument: string;
    type: CryptoOrderTransaction;
    amount: number;
    price: number;
    isLimit: boolean;
    isStop: boolean;
    useFeeDiscount: boolean;
    selfMatchStrategy: number;
    selfMatchToken: string;
    side: number;
    orderType: number;
    requestedQuoteAmount?: number;
}

@injectable()
export class AxosCryptoTradingApi extends TradingApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.UserApi) protected override readonly userApi: UserApi,
        @inject(ApiInjectToken.QuoteApi) protected override readonly quoteApi: QuoteApi,
    ) {
        super(apis, config, userApi, quoteApi);
    }

    protected override prepareOrder(values: TradingFormValues, meta: TradingFormMeta) {
        const orderBody = super.prepareOrder(values, meta, false);

        if (meta.mutualFundTrading) {
            orderBody.order.capitalGains = values.mutualFundCapitalGains;
            orderBody.order.dividends = values.mutualFundDividends;
        }

        return orderBody;
    }

    private async prepareAxosCryptoOrder(values: CryptoTradingFormValues): Promise<AxosCryptoTradingValues> {
        const formatOptions: numbro.Format = {
            mantissa: 4,
            roundingFunction: Math.floor,
        };

        if (values.tradeWithStablecoin && values.orderType === OrderType.Limit) {
            return {
                instrument: values.coin,
                type: values.transaction,
                amount: numbro.unformat(
                    numbro(divide(values.quantity ?? 0, values.limitPrice ?? 0)).format(formatOptions),
                ),
                price: values.limitPrice || 0,
                isLimit: true,
                isStop: false,
                useFeeDiscount: true,
                selfMatchStrategy: 0,
                selfMatchToken: getUuid(),
                side: 0,
                orderType: 0,
            };
        }
        if (values.tradeWithStablecoin) {
            return {
                instrument: values.coin,
                type: values.transaction,
                amount: 0,
                price: values.orderType === OrderType.Limit ? values.limitPrice || 0 : 0,
                isLimit: values.orderType === OrderType.Limit,
                isStop: values.orderType === OrderType.Stop,
                useFeeDiscount: true,
                selfMatchStrategy: 0,
                selfMatchToken: getUuid(),
                side: 0,
                orderType: 0,
                requestedQuoteAmount: values.quantity,
            };
        }

        return {
            instrument: values.coin,
            type: values.transaction,
            amount: values.quantity || 0,
            price: values.orderType === OrderType.Limit ? values.limitPrice || 0 : 0,
            isLimit: values.orderType === OrderType.Limit,
            isStop: values.orderType === OrderType.Stop,
            useFeeDiscount: true,
            selfMatchStrategy: 0,
            selfMatchToken: getUuid(),
            side: 0,
            orderType: 0,
        };
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    override async previewCryptoOrder(
        _values: CryptoTradingFormValues,
        _accountNumber: string,
    ): Promise<CryptoTradePreview> {
        return {
            expectedQty: 0,
            expectedPx: 0,
            warnings: [],
            orderFeesCharges: 0,
        };
    }

    /* eslint-enable @typescript-eslint/no-unused-vars */

    override async placeCryptoOrder(
        values: CryptoTradingFormValues,
        accountNumber: string,
    ): Promise<OrderWithExtraInfo> {
        const order = await this.prepareAxosCryptoOrder(values);
        order.instrument = parseCryptoSymbol(order.instrument, '_').symbol.toLowerCase();
        const requestId = getUuid();
        const response = await this.apis.middleware.getApiClient().post<any>('axos/crypto/trading/orders', {
            requestId,
            requests: [{ accountNumber, ...order }],
        });
        return { orderRef: requestId, ...response.data.entity[0].order };
    }

    override async getCryptoOrderDetails(accountId: number, orderRef: string): Promise<OrderWithExtraInfo | null> {
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/trading/orders', {
            params: {
                accountId,
                requestId: orderRef,
            },
        });
        if (!response.data?.entity) {
            return null;
        }
        const order = response.data?.entity?.[0];
        return {
            validity: OrderValidity.GoodForDay,
            marketTime: OrderMarketTime.CoreMarket,
            execTime: order.createdAt,
            symbol: order.instrument,
            ourRef: order.orderId,
            transType: OrderActualTransaction.Buy,
            market: OrderMarket.Us,
            theirRef: '',
            clientRef: '',
            contraOid: 0,
            aon: 0,
            margin: 0,
            requestState: 0,
            accountType: AccountType.Cash,
            accountId: 0,
            optionFlag: 0,
            legRef: 0,
            noLegs: 0,
            expectedValue: 0,
            quantity: order?.requestedQuoteAmount === 0 ? order?.amount : order?.requestedQuoteAmount,
            execQty: 0,
            remainingQty: 0,
            unallocatedQty: 0,
            limitPx: order.limitPrice,
            stopPx: order.stopPrice,
            execPx: order.executionPrice,
            expectedPx: order.expectedPx,
            expectedQty: 0,
            replaceable: false,
            createDate: '',
            executedDate: order.createdAt,
            option: false,
            mutualFund: false,
            translatedStatus: OrderStatus.AcceptedAtTheExchange,
            multilegOrderType: '',
            marketSpreadPrice: 0,
            executedSpreadPrice: 0,
            expectedSpreadPx: 0,
            cancelable: false,
            algoOrder: false,
            allocation: false,
            actualTransaction: OrderActualTransaction.Buy,
            modelId: 0,
            orderRef: order.orderId,
            companyName: '',
            model: '',
            fullName: '',
            accountNumber: '',
            tradeType: '',
            securityType: '',
            value: 0,
            costValue: 0,
            transaction: OrderTransaction.Buy,
            commission: order.commission,
            commissionCurrency: order.commissionCurrency,
            createdBy: order.createdBy,
            isLimit: order.isLimit,
            isPending: order.isPending,
            orderType: order?.orderType,
            remaining: order.remaining,
            status: order.status,
            tradedAmount: order.tradedAmount,
            unitsFilled: order?.unitsFilled ?? subtract(order?.quantity ?? 0, order?.remainingQty ?? 0),
            rejectDetails: order.rejectDetails,
            cancelReason: order.cancelReason,
            requestId: order.requestId,
            messages: response.data.status !== 200 ? order.messages : [],
            total: sum(
                multiply(order.executionPrice, order.tradedAmount),
                // When selling, commission comes back as USD. When buying, commission value is in coin traded
                order.type === CryptoOrderTransaction.Sell
                    ? order.commission
                    : multiply(order.executionPrice, order.commission),
            ),
            bond: false,
            accruedInterest: 0,
            tradeCurrency: order.tradeCurrency || 0,
            accountCurrency: order.accountCurrency || 0,
        };
    }

    override async cancelCryptoOrder(accountNumber: string, orderId: string): Promise<CancelledCryptoOrderInfo> {
        const requestId = getUuid();
        const response = await this.apis.middleware.getApiClient().delete<any>('axos/crypto/trading/orders', {
            data: {
                requestId,
                requests: [
                    {
                        accountNumber,
                        orderId,
                    },
                ],
            },
        });
        return response.data.entity[0];
    }
}
